import { Button, FormControl, FormLabel, Input, Stack, Text, useToast, VStack } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { changePassword } from "../../api";
import LogInOnly from "../../components/Auth/LogInOnly";
import { colors } from "../../styles";
import { getApiErrorCode, getApiErrorMessage } from "../../utils";

export default function ChangePassword() {

    /* queryClient */

    const queryClient = useQueryClient();
    const navigate = useNavigate();

    /* useForm */

    const toast = useToast();

    /* IChangePasswordForm */

    interface IChangePasswordForm {
        legacyPassword : string;
        newPassword : string;
        newPasswordCheck : string;
    }

    const {register, handleSubmit, formState: {errors}, watch} = useForm<IChangePasswordForm>();

    /* passwordRegex */

    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&~])[A-Za-z\d@$!%*#?&~]{8,20}$/;
    /* isLegacyPasswordRegexFilledRight */

    const isLegacyPasswordFilled = Boolean(watch("legacyPassword"));
    const isNewPasswordRegexFilledRight = passwordRegex.test(watch("newPassword"));

   /* isValidPassword */

   const isValidPassword = (newPassword:string, newPasswordCheck:string) => {
       if(newPassword === newPasswordCheck) {
           return true;
       } else {
           return false;
       }
    };

    /* isNewPasswordFilledRight */
   
   const isNewPasswordFilledRight = isValidPassword(watch("newPassword"), watch("newPasswordCheck")) && isNewPasswordRegexFilledRight;

    ////////////////////////////////////////////////////////////////////// 

    const onSubmit = async (data:IChangePasswordForm) => {

        ////////////////////////////////////////////////////////////////////// 

        const { legacyPassword, newPassword } = data;

        ////////////////////////////////////////////////////////////////////// 비밀번호 유효성 검사

        if(!isLegacyPasswordFilled || !isNewPasswordFilledRight) return;

        ////////////////////////////////////////////////////////////////////// 비밀번호 변경
        
        await ChangePasswordMutation.mutateAsync({
            legacyPassword,
            newPassword,
        });
        
    }

    /* ChangePasswordMutation */ 

    const ChangePasswordMutation = useMutation({
        mutationFn: changePassword,
        onSuccess: (data) => {

            ////////////////////////////////////////////////////////////////////// queryClient
            
            queryClient.invalidateQueries({queryKey:['me']});

            ////////////////////////////////////////////////////////////////////// toast
            toast({
                title: '비밀번호 재설정 완료',
                status: 'success',
                position: 'top',
                duration: 2000,
                isClosable: true,
            });

            ////////////////////////////////////////////////////////////////////// navigate

            navigate("/me");

        },
        onError: (error) => {
            console.log(error);
            toast({
                title: getChangePasswordErrorMessage(error),
                status: 'error',
                position: 'top',
                duration: 3000,
            });
        }
    });

    const getChangePasswordErrorMessage = (error: unknown) => {
        // 서버 code: INVALID_CURRENT_PASSWORD | PASSWORD_POLICY_VIOLATION | PASSWORD_SAME_AS_OLD
        switch (getApiErrorCode(error)) {
            case "INVALID_CURRENT_PASSWORD":
                return "기존 비밀번호가 일치하지 않습니다.";
            case "PASSWORD_POLICY_VIOLATION":
                return "새 비밀번호 형식이 올바르지 않습니다.";
            case "PASSWORD_SAME_AS_OLD":
                return "기존 비밀번호와 다른 비밀번호를 입력해주세요.";
            default:
                return getApiErrorMessage(error, "비밀번호 재설정 실패");
        }
    };

    /* rendering */

    return (
        <LogInOnly>
            <Stack justifyContent={"center"} alignItems={"center"} width={"100%"} paddingY={20}>
                <Stack 
                    width={{
                        base:"100%",
                        sm:"100%",
                        md:"60%",
                        lg:"40%",
                        xl:"40%"
                    }} 
                >
                    <VStack  
                        padding={1}
                        spacing={0}
                        alignItems={"flex-start"}
                        width={"100%"} 
                    >
                        <Text fontSize={28} fontWeight={700} color={colors.g1}>비밀번호 변경하기</Text>
                    </VStack>
                    <VStack 
                        marginY={5}
                        padding={5}
                        alignItems={"flex-start"}
                    >
                        <FormControl marginBottom={10}>
                            <FormLabel fontSize="sm" color={colors.blue}>
                                기존 비밀번호
                            </FormLabel>
                            <Input 
                                {...register("legacyPassword", {required:true})} 
                                type="password" 
                                isInvalid={Boolean(errors.legacyPassword?.message)} 
                                placeholder={"기존 비밀번호 입력"}
                                borderBottom={`1px solid ${colors.g4}`} 
                                borderTop="none" 
                                borderLeft="none" 
                                borderRight="none" 
                                borderRadius="0" 
                                _focus={{
                                    borderBottom: `1px solid ${colors.blue}`,
                                    boxShadow: "none",
                                    outline: "none"
                                }}
                            />
                        </FormControl>
                        <FormControl marginTop={5}>
                            <FormLabel fontSize="sm" color={colors.blue}>
                                새로운 비밀번호
                            </FormLabel>
                            <Input 
                                {...register("newPassword", {required:true})} 
                                type="password" 
                                isInvalid={Boolean(errors.newPassword?.message)} 
                                placeholder={"새로운 비밀번호 입력"} 
                                borderBottom={`1px solid ${colors.g4}`} 
                                borderTop="none" 
                                borderLeft="none" 
                                borderRight="none" 
                                borderRadius="0" 
                                _focus={{
                                    borderBottom: `1px solid ${colors.blue}`,
                                    boxShadow: "none",
                                    outline: "none"
                                }}
                            />
                        </FormControl>
                        <Text color={colors.g3} textAlign={"start"} fontSize={"xs"}> 비밀번호는 영문, 숫자, 특수문자를 포함한 8자 이상이어야 합니다. </Text>
                        <FormControl marginTop={5}>
                            <FormLabel fontSize="sm" color={colors.blue}>
                                새로운 비밀번호 확인
                            </FormLabel>
                            <Input 
                                {...register("newPasswordCheck", {required:true})} 
                                type="password" 
                                isInvalid={Boolean(errors.newPasswordCheck?.message)} 
                                placeholder={"새로운 비밀번호 확인"} 
                                borderBottom={`1px solid ${colors.g4}`} 
                                borderTop="none" 
                                borderLeft="none" 
                                borderRight="none" 
                                borderRadius="0" 
                                _focus={{
                                    borderBottom: `1px solid ${colors.blue}`,
                                    boxShadow: "none",
                                    outline: "none"
                                }}
                            />
                        </FormControl>
                        
                        <Button 
                            width={"100%"} 
                            height={"52px"}
                            backgroundColor={colors.blue} 
                            color={"white"} 
                            borderRadius={10}
                            marginTop={10}
                            onClick={handleSubmit(onSubmit)}
                            disabled={!isLegacyPasswordFilled || !isNewPasswordFilledRight}
                            isLoading={ChangePasswordMutation.isPending}
                        >
                            <Text fontSize={16} fontWeight={700}>변경하기</Text>
                        </Button>
                    </VStack>
                </Stack>
            </Stack>
        </LogInOnly>
    )
}